import { useMemo } from "react";
import { Link } from "wouter";
import { format, formatDistanceToNow, isThisWeek, isToday, isTomorrow } from "date-fns";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout/AppLayout";
import { DocumentList } from "@/components/dashboard/DocumentList";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, CalendarDays, FilePlus2, FileText, Quote } from "lucide-react";
import { Note } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { stripMarkdown } from "@/lib/markdown";
import { useDocuments, useFolders, useUpcomingSermons } from "@/hooks/use-library";

/** How many documents the Recent list shows before handing off to the library. */
const RECENT_LIMIT = 6;

function greetingForHour(hour: number) {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** "Today" / "Tomorrow" / "Sunday" / "24 Aug" — nearest reading first. */
function deliveryLabel(value: string) {
  const date = new Date(value);
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  if (isThisWeek(date, { weekStartsOn: 1 })) return format(date, "EEEE");
  return format(date, "EEE d MMM");
}

/**
 * A note's own words, not its parent's name.
 *
 * The parent used to be the heading, so five notes on one sermon rendered as
 * five identical rows. The note's first line is what tells them apart.
 */
function noteHeadline(note: Note) {
  const [firstLine] = stripMarkdown(note.content).trim().split("\n");
  return firstLine?.trim() || "Empty note";
}

/** Where the note came from — context, so it belongs under the headline. */
function noteSource(note: Note) {
  switch (note.parent_type) {
    case "document":
      return note.document?.title || "Document";
    case "scripture":
      return note.scripture_reference?.book
        ? `${note.scripture_reference.book} ${note.scripture_reference.chapter}:${note.scripture_reference.verse_start}`
        : "Scripture";
    case "quote":
      return note.quote?.author ? `Quote by ${note.quote.author}` : "Quote";
    default:
      return "Loose note";
  }
}

function noteLink(note: Note) {
  switch (note.parent_type) {
    case "document":
      return note.parent_id ? `/documents/${note.parent_id}/edit` : `/notes/${note.id}`;
    case "scripture":
      return note.parent_id
        ? `/scripture-references/${note.parent_id}`
        : `/notes/${note.id}`;
    case "quote":
      return note.parent_id ? `/quotes/${note.parent_id}` : `/notes/${note.id}`;
    default:
      return `/notes/${note.id}`;
  }
}

function NoteIcon({ note }: { note: Note }) {
  const className = "h-4 w-4 shrink-0 text-muted-foreground";
  if (note.parent_type === "scripture") return <BookOpen className={className} />;
  if (note.parent_type === "quote") return <Quote className={className} />;
  return <FileText className={className} />;
}

function SectionHeading({
  title,
  href,
  linkLabel,
}: {
  title: string;
  href?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-4">
      <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </h2>
      {href && (
        <Link href={href}>
          <span className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
            {linkLabel} →
          </span>
        </Link>
      )}
    </div>
  );
}

/**
 * The dashboard answers three questions and deliberately nothing else:
 * what was I in the middle of, what is coming up, and what did I touch
 * recently. Folders, tags, types and archive are organisation — they live in
 * the sidebar and the library, which is why this page has no filter UI at all.
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);

  const { data: documents = [], isLoading: documentsLoading } = useDocuments();
  const { data: folders = [] } = useFolders();
  const { data: upcoming = [] } = useUpcomingSermons();

  const { data: notes = [], isLoading: notesLoading } = useQuery({
    queryKey: ["/api/notes"],
    queryFn: () => get<Note[]>("/notes"),
  });

  // The API already orders by updated_at desc, but sorting here keeps the
  // hero honest if that ever changes.
  const sorted = useMemo(
    () =>
      [...documents].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [documents],
  );

  const continueDoc = sorted[0];
  // The hero *is* the most recent document, so leaving it in the list below
  // printed the same row twice on the same screen.
  const recentDocuments = sorted.slice(1, 1 + RECENT_LIMIT);

  const recentNotes = useMemo(
    () =>
      [...notes]
        .sort(
          (a, b) =>
            new Date(b.updated_at || b.created_at).getTime() -
            new Date(a.updated_at || a.created_at).getTime(),
        )
        .slice(0, 5),
    [notes],
  );

  const upcomingWithDocs = upcoming.filter((sermon) => sermon.document);

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl px-6 py-10 md:px-8">
        <header className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
            {greeting}
            {user?.username ? `, ${user.username}` : ""}
          </h1>
          <Link href="/documents/create">
            <Button>
              <FilePlus2 className="mr-2 h-4 w-4" />
              New document
            </Button>
          </Link>
        </header>

        {continueDoc && (
          <Link href={`/documents/${continueDoc.id}/edit`}>
            <div className="group mb-10 cursor-pointer rounded-xl border border-border bg-muted/30 px-5 py-6 transition-colors hover:bg-muted/50 sm:px-7 sm:py-7">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Continue writing
                  </p>
                  <h2 className="truncate text-2xl font-medium group-hover:underline">
                    {continueDoc.title || "Untitled"}
                  </h2>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {continueDoc.doc_type || "Document"}
                    {continueDoc.word_count
                      ? ` · ${continueDoc.word_count.toLocaleString()} words`
                      : ""}
                    {" · edited "}
                    {formatDistanceToNow(new Date(continueDoc.updated_at), {
                      addSuffix: true,
                    })}
                  </p>
                </div>
                <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
              </div>
            </div>
          </Link>
        )}

        {/* Two columns once there is genuinely room for them. Notes are
            secondary but worth seeing without scrolling, so they take a rail
            rather than the bottom of the page. */}
        <div className="grid gap-x-10 gap-y-10 xl:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0 space-y-10">
            {upcomingWithDocs.length > 0 && (
              <section>
                <SectionHeading title="Next up" />
                <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                  {upcomingWithDocs.map((sermon) => (
                    <li key={sermon.id ?? sermon.document_id}>
                      <Link href={`/documents/${sermon.document!.id}/edit`}>
                        <div className="flex cursor-pointer items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40">
                          <div className="flex w-24 shrink-0 items-center gap-2 text-sm font-medium">
                            <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">
                              {deliveryLabel(sermon.delivery_date!)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium">
                              {sermon.document!.title || "Untitled"}
                            </p>
                            {sermon.series_name && (
                              <p className="truncate text-sm text-muted-foreground">
                                {sermon.series_name}
                              </p>
                            )}
                          </div>
                          <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                            {sermon.document!.word_count
                              ? `${sermon.document!.word_count.toLocaleString()} words`
                              : "Not started"}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <SectionHeading title="Recent" href="/library" linkLabel="All documents" />
              <DocumentList
                documents={recentDocuments}
                folders={folders}
                showFolder
                isLoading={documentsLoading && documents.length === 0}
                emptyTitle={
                  continueDoc ? "Nothing else recent." : "Nothing written yet."
                }
                emptyHint={
                  continueDoc
                    ? undefined
                    : "Start a document and it will be waiting here next time."
                }
                showCreateOnEmpty={!continueDoc}
              />
            </section>
          </div>

          <aside className="min-w-0">
            <div className="xl:sticky xl:top-10">
              <SectionHeading title="Recent notes" href="/notes" linkLabel="All notes" />

              {notesLoading ? (
                <p className="py-6 text-sm text-muted-foreground">Loading notes…</p>
              ) : recentNotes.length > 0 ? (
                <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                  {recentNotes.map((note) => (
                    <li key={note.id}>
                      <Link href={noteLink(note)}>
                        <div className="flex cursor-pointer items-start gap-2.5 px-3.5 py-3 transition-colors hover:bg-muted/40">
                          <span className="mt-0.5">
                            <NoteIcon note={note} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {noteHeadline(note)}
                            </p>
                            {/* Source and time share a line: the rail is
                                narrow, and neither is worth its own row. */}
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">
                              {noteSource(note)} ·{" "}
                              {formatDistanceToNow(
                                new Date(note.updated_at || note.created_at),
                                { addSuffix: true },
                              )}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-xl border border-dashed border-border px-5 py-8 text-center text-sm text-muted-foreground">
                  No notes yet. Capture thoughts from the editor or the Notes page.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  );
}
